import {
  addDoc,
  collection,
  doc,
  updateDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from "firebase/firestore";

import { db } from "../../../firebase";

import {
  generatePONumber
} from "./poNumberService";

/*

  RATE BREAKDOWN — LANDED COST (REFERENCE ONLY)

  Never touches `rate` — that field is GRN's own, entered fresh at GRN
  time per CORE_RULES §6 ("Rate saved at PO time is FORBIDDEN — rate at
  GRN time only"). base_rate/discount/freight/GST here are a SEPARATE,
  additive reference figure shown on PO / Vendor Assignment screens so a
  Manager can judge true cost across vendors — comparing base rate alone
  is misleading when discount/freight/GST differ between vendors.

  Freight is entered ONCE per PO (one shipment cost) and split across
  items proportionally by each item's post-discount subtotal.

*/

export const calculateItemDiscount = (baseRate, qty, discountType, discountValue) => {
  const subtotal = Number(baseRate || 0) * Number(qty || 0);
  const value    = Number(discountValue || 0);
  if (discountType === "percent") {
    return subtotal * (value / 100);
  }
  // "flat" — a flat ₹ discount is a total for this line, not per-unit
  return Math.min(value, subtotal); // never let discount exceed the subtotal
};

/*

  LOWEST-RATE SUGGESTION — cross-vendor rate history (reference only)

  Reads the SAME base_rate/discount/gst/landed_rate fields Rate Breakdown
  already writes on every purchase_orders doc — no new collection, no new
  write path. Company-scoped, last 12 months, fetched ONCE per page load
  (same pattern as itemsMaster) and matched client-side per item, instead
  of a fresh query per item selection — avoids the N+1 anti-pattern this
  codebase already guards against elsewhere (ProcurementQueue etc.).

  NOTE: POs created before the Rate Breakdown feature existed will show
  landed_rate === base_rate (no discount/freight/GST was ever captured for
  them) — still a useful plain-rate reference, just not a true landed
  figure. Not flagged specially; the date next to each entry makes this
  self-evident to whoever's reading it.

*/

export const fetchRecentPOHistory = async (company_id) => {
  if (!company_id) return [];
  try {
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

    const snap = await getDocs(query(
      collection(db, "purchase_orders"),
      where("company_id", "==", company_id),
      orderBy("created_at", "desc"),
      limit(300) // pragmatic cap — revisit if a company's PO volume grows well past this
    ));

    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(po => {
        if (!po.created_at) return false;
        const createdDate = po.created_at instanceof Timestamp ? po.created_at.toDate() : new Date(po.created_at);
        return createdDate >= twelveMonthsAgo;
      });
  } catch (error) {
    console.error("[fetchRecentPOHistory] failed:", error);
    return []; // fail soft — the suggestion feature is a nice-to-have, never blocks PO creation
  }
};

// Returns up to 5 historical (vendor, rate, date) entries for one item,
// sorted by landed_rate ascending (cheapest first). Matches by item_id
// when it's a real Item Master item; falls back to normalized name match
// for custom items, whose item_id is a fresh random string every time.
export const getItemRateHistory = (poHistory, item) => {
  if (!item?.item_name?.trim()) return [];
  const targetName = item.item_name.toLowerCase().trim().replace(/\s+/g, " ");
  const targetId    = item.item_id;

  const entries = [];
  for (const po of poHistory) {
    for (const poItem of (po.items || [])) {
      if (poItem._cancelled) continue;
      const nameMatches = (poItem.item_name || "").toLowerCase().trim().replace(/\s+/g, " ") === targetName;
      const idMatches    = targetId && poItem.item_id === targetId && !poItem.is_custom_item;
      if (!idMatches && !nameMatches) continue;

      entries.push({
        po_number:   po.po_number || po.id,
        vendor_name: po.vendor_name || "Unknown vendor",
        created_at:  po.created_at,
        base_rate:   Number(poItem.base_rate || poItem.rate || 0),
        landed_rate: Number(poItem.landed_rate || poItem.base_rate || poItem.rate || 0),
      });
    }
  }

  return entries
    .filter(e => e.landed_rate > 0)
    .sort((a, b) => a.landed_rate - b.landed_rate)
    .slice(0, 5);
};

export const calculateLandedCost = (items = [], freightAmount = 0) => {
  const freight = Number(freightAmount || 0);

  // Pass 1 — post-discount subtotal per item (needed to proportion freight)
  const afterDiscount = items.map((item) => {
    const qty      = Number(item.qty || item.ordered_qty || 0);
    const subtotal = Number(item.base_rate || 0) * qty;
    const discount = calculateItemDiscount(item.base_rate, qty, item.discount_type, item.discount_value);
    return Math.max(subtotal - discount, 0);
  });

  const totalAfterDiscount = afterDiscount.reduce((s, v) => s + v, 0);

  // Pass 2 — freight share + GST → landed total + landed rate/unit
  return items.map((item, idx) => {
    const qty               = Number(item.qty || item.ordered_qty || 0);
    const itemAfterDiscount = afterDiscount[idx];
    const freightShare      = totalAfterDiscount > 0
      ? (itemAfterDiscount / totalAfterDiscount) * freight
      : 0;
    const gstPercent        = Number(item.gst_percent || 0);
    const landedSubtotal    = itemAfterDiscount + freightShare;
    const landedTotal       = landedSubtotal * (1 + gstPercent / 100);
    const landedRate        = qty > 0 ? landedTotal / qty : 0;
    const baseSubtotal      = Number(item.base_rate || 0) * qty;

    return {
      ...item,
      discount_amount:  Number((baseSubtotal - itemAfterDiscount).toFixed(2)),
      freight_share:    Number(freightShare.toFixed(2)),
      landed_subtotal:  Number(landedSubtotal.toFixed(2)),
      landed_total:     Number(landedTotal.toFixed(2)),
      landed_rate:      Number(landedRate.toFixed(2)),
    };
  });
};

/*

  CREATE PURCHASE ORDER

*/

export const createPurchaseOrder =
  async (payload) => {

    try {

      // GENERATE PO NUMBER

      const poNumber =
        await generatePONumber();

      // NORMALIZE ITEMS

      const normalizedItems =

  (payload.items || []).map(
    (item, index) => {

      const orderedQty =
        Number(
          item.qty ||
          item.ordered_qty ||
          0
        );

      // CUSTOM ITEM SUPPORT — item_id missing/blank means it's a custom
      // (hand-typed, not in Item Master) item. A stable placeholder ID is
      // generated HERE ONLY — single source, no other file duplicates this
      // logic. is_custom_item carries through so GRN can apply the correct
      // rule: custom items are received for record-keeping only, never
      // matched to Item Master, never credited to inventory_summary
      // (Constitution §5 / CORE_RULES §6).
      const isCustom =
        !!item.is_custom_item || !item.item_id;

      const resolvedItemId =
        item.item_id ||
        `custom_${Date.now()}_${index}`;

      return {

        // LINE TRACKING

        line_id:
          `${resolvedItemId}_${index}`,

        // MATERIAL

        item_id:
          resolvedItemId,

        item_name:
          item.item_name || "",

        item_code:
          item.item_code || "",

        item_category:
          item.item_category || "",

        is_custom_item:
          isCustom,

        // QUANTITY

        qty: orderedQty,

        ordered_qty:
          orderedQty,

        received_qty: 0,

        pending_qty:
          orderedQty,

        rejected_qty: 0,

        // RECEIVE STATUS

        item_receive_status:
          "pending",

        item_status:
          "pending",

        // UNIT

        unit:
          item.unit ||
          item.uom ||
          "nos",

        // FINANCIALS

        rate:
          Number(item.rate || 0),

        // RATE BREAKDOWN (reference only — never feeds GRN's own `rate`)
        base_rate:
          Number(item.base_rate || item.rate || 0),

        discount_type:
          item.discount_type === "flat" ? "flat" : "percent",

        discount_value:
          Number(item.discount_value || 0),

        gst_percent:
          Number(
            item.gst_percent || 0
          ),

        amount:
          Number(item.amount || 0)

      };
    }
  );

      // RATE BREAKDOWN — compute landed cost per item (reference only)
      const freightAmount =
        Number(payload.freight_amount || 0);

      const itemsWithLandedCost =
        calculateLandedCost(normalizedItems, freightAmount);

      // CALCULATIONS

      const subtotal =

        normalizedItems.reduce(

          (sum, item) =>

            sum + item.amount,

          0
        );

      // Reference-only total — Base Rate × Qty, minus discount, plus
      // proportioned freight, plus GST. NEVER used for GRN/grand_total —
      // that stays received_qty × rate, set fresh at GRN time.
      const landedGrandTotal =
        itemsWithLandedCost.reduce(
          (sum, item) => sum + (item.landed_total || 0),
          0
        );

      // FINAL DOCUMENT

      const purchaseOrderDoc = {

  // MULTI TENANT

  company_id:
    payload.company_id,

        // PURCHASE MODE (Priority 4, Direct Site Purchase)
        // "stock_purchase" (default) → unchanged existing flow.
        // "direct_site_purchase" → vendor delivers to a project site;
        //   Site Engineer confirms qty, Manager/SM/Owner finalizes
        //   with rate; only site_inventory_summary is credited,
        //   central inventory_summary is NEVER touched.
        purchase_mode:
          payload.purchase_mode === "direct_site_purchase"
            ? "direct_site_purchase"
            : "stock_purchase",

        project_id:
          payload.project_id || "",

        project_name:
          payload.project_name || "",

        site_id:
          payload.site_id || "",

        site_name:
          payload.site_name || "",

        // Two-stage site confirmation — only meaningful for
        // direct_site_purchase; stock_purchase POs stay
        // "not_applicable" forever.
        site_confirmation_status:
          payload.purchase_mode === "direct_site_purchase"
            ? "pending_site_confirmation"
            : "not_applicable",

        site_confirmed_items:   [],
        site_confirmed_by:      "",
        site_confirmed_by_name: "",
        site_confirmed_at:      null,

        // Per-PO Site Engineer override (Priority 4 refinement) — empty
        // array means "fall back to the project's whole assigned team"
        // (existing behaviour). Non-empty means ONLY these specific
        // engineers can see/confirm THIS PO, even if the project has
        // others assigned too. Lets a Manager pin a delivery to whoever
        // is actually on-site right now, without touching the project's
        // broader site_engineer_ids[] assignment.
        assigned_site_engineer_ids:
          payload.purchase_mode === "direct_site_purchase"
            ? (payload.assigned_site_engineer_ids || [])
            : [],

        // IDENTIFICATION

        po_number: poNumber,

        po_status: "draft",

        approval_status: "pending",

        receive_status: "pending",

        // payment_status removed (D-289) — a PO's payment state is CALCULATED from the vendor khaata, never stored.

        // TIMESTAMPS

        created_at:
          serverTimestamp(),

        updated_at:
          serverTimestamp(),

        // VENDOR

        vendor_id:
          payload.vendor_id || "",

        vendor_name:
          payload.vendor_name || "",

        vendor_email:
          payload.vendor_email || "",

        vendor_phone:
          payload.vendor_phone || "",

        vendor_address:
          payload.vendor_address || "",

        vendor_gst:
          payload.vendor_gst || "",

        // PROCUREMENT SOURCE

        source_purchase_request_id:

          payload
            .source_purchase_request_id || "",

        // source_type stays "procurement_queue" by default (unchanged
        // behavior for VendorAssignmentModal, which never passes either
        // field) — but now driven by payload so a Direct PO doesn't get
        // silently mislabeled as procurement_queue-originated.
        source_type:
          payload.source_type || payload.source || "procurement_queue",

        // Additive tag, separate from source_type. CreateDirectPO.js passes
        // source:"direct" — any UI reading po.source === "direct" keeps
        // working after consolidation.
        source:
          payload.source || "",

        // GRN / RECEIPT STATE FLAGS — additive, same defaults
        // CreateDirectPO.js's own PO doc previously set directly.
        grn_locked:
          false,

        over_received:
          false,

        short_closed:
          false,

        // DELIVERY

        delivery_date:
          payload.delivery_date || "",

        priority:
          payload.priority || "normal",

        transport:
          payload.transport || "",

        remarks:
          payload.remarks || "",

        // Additive — separate from `remarks`; CreateDirectPO.js's own
        // "Notes" field maps here.
        notes:
          payload.notes || "",

        // AUDIT — additive. Callers that don't pass these get "" exactly
        // as before (created_by/created_by_name were never set at all
        // previously by this function — this only adds them).
        created_by:
          payload.created_by || "",

        created_by_name:
          payload.created_by_name || "",

        updated_by:
          payload.created_by || "",

        // FINANCIALS

        subtotal,

        gst_total: 0,

        discount_total: 0,

        grand_total: subtotal,

        // RATE BREAKDOWN — reference only, never drives GRN/grand_total
        freight_amount:
          freightAmount,

        landed_grand_total:
          Number(landedGrandTotal.toFixed(2)),

        // TRACKING

        total_items:
          normalizedItems.length,

        received_items: 0,

        pending_items:
          normalizedItems.length,

        completion_percentage: 0,

        // ITEMS

        items: itemsWithLandedCost

      };

      // CREATE PURCHASE ORDER

      const purchaseOrderRef =
        await addDoc(

          collection(
            db,
            "purchase_orders"
          ),

          purchaseOrderDoc
        );

      // UPDATE SOURCE REQUEST

      if (
        payload
          .source_purchase_request_id
      ) {

        await updateDoc(

          doc(
            db,
            "purchase_requests",
            payload.source_purchase_request_id
          ),

          {

            po_created: true,

            po_number: poNumber,

            po_id:
              purchaseOrderRef.id,

            status: "po_created",

            updated_at:
              serverTimestamp()

          }
        );
      }

      console.log(
        "PURCHASE ORDER CREATED",
        purchaseOrderRef.id
      );

      return {

        success: true,

        po_id:
          purchaseOrderRef.id,

        po_number: poNumber,

        data: purchaseOrderDoc

      };

    } catch (error) {

      console.error(

        "Purchase order creation failed",

        error

      );

      return {

        success: false,

        error

      };
    }
  };